import { Box, Divider, Stack, Typography } from '@mui/material';
import { Provider, type GcpNetworkConnectivityConfig } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { getNetworkLabel } from '@/components/selectors/gcpNetworkSelect';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { DeleteRestoreServerConfigDialog } from './deleteRestoreServerConfigDialog';
import { GcpCustomizeNetworkBottomDialog } from './gcpCustomizeNetworkBottomDialog';
import { GcpNetworkSelectionDialog } from './gcpNetworkSelectionDialog';
import type { RestoreAccountConfigState } from './restoreAccountConfigFlow';

export const GcpRestoreAccountConfigVpcSelection = (
  props: StepProps<RestoreAccountConfigState>
) => {
  const dal = useDAL();
  const [
    addCustomNetworkSettingsButtonEl,
    setAddCustomNetworkSettingsButtonEl,
  ] = useState<HTMLElement | null>(null);
  const [configForDeletion, setConfigForDeletion] = useState<
    GcpNetworkConnectivityConfig | undefined
  >(undefined);
  const [showVpcSelectionDialog, setShowVpcSelectionDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<
    GcpNetworkConnectivityConfig | undefined
  >();
  const [editedConfig, setEditedConfig] = useState<
    GcpNetworkConnectivityConfig | undefined
  >();

  const { data: regions } = dal.restore.regions.useList(
    props.wizardState.account.id
  );

  const sortedConfigs = useMemo(() => {
    return (props.wizardState.config.config.gcp?.networkConfigs || []).sort(
      (a, b) => a.network.localeCompare(b.network)
    );
  }, [props.wizardState.config.config.gcp?.networkConfigs]);

  return (
    <StepContainer
      hideStepper
      onBackClick={props.back}
      canGoNext={() => true}
      nextButtonText={'Save Changes'}
      backButtonText='Cancel'
      onNextClick={() => props.finish()}
      sx={{ height: '100%' }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack className='px-[50px]' direction='row' flexGrow={1}>
          <Stack className='w-[280px]' gap='40px'>
            {!!props.wizardState.config.config.gcp?.networkConfigs?.length && (
              <Stack gap='40px'>
                <Typography>Customized Networks</Typography>
                <Stack gap='24px'>
                  {sortedConfigs.map((config) => (
                    <Stack
                      key={`${config.network}-${config.networkHostProject}`}
                      gap='24px'
                    >
                      <Stack
                        justifyContent='space-between'
                        direction='row'
                        alignItems='center'
                        className='pr-[22px] cursor-pointer'
                        sx={{
                          ...(selectedConfig === config
                            ? { borderRight: '2px solid black' }
                            : { borderRight: '2px solid transparent' }),
                        }}
                        onClick={() => setSelectedConfig(config)}
                      >
                        {/* here */}
                        <Typography>
                          {getNetworkLabel({
                            networkName: config.network,
                            project: config.networkHostProject,
                            isShared: config.isSharedVpc,
                          })}
                        </Typography>
                        <OptionMenu
                          options={[
                            {
                              id: 'delete-config',
                              text: 'Delete',
                              menuItemProps: {
                                onClick: () => setConfigForDeletion(config),
                              },
                            },
                          ]}
                        />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
            <Box>
              <ButtonWithIcon
                ref={setAddCustomNetworkSettingsButtonEl}
                variant='outlined'
                icon='material-symbols-add-rounded'
                text={
                  props.wizardState.config.config.gcp?.networkConfigs?.length
                    ? 'Customize Another Network'
                    : 'Customize Network'
                }
                onClick={() => setShowVpcSelectionDialog(true)}
              />
            </Box>
            {!!props.wizardState.config.config.gcp?.networkConfigs?.length && (
              <>
                <Divider className='mt-[20px] ml-[-100px]' />
                <Typography
                  className='w-[208px]'
                  fontWeight={'300'}
                  lineHeight={'20px'}
                >
                  For all other networks, Eon chooses a suitable subnet.
                </Typography>
              </>
            )}
            <DeleteRestoreServerConfigDialog
              name={configForDeletion?.network || ''}
              provider={Provider.Gcp}
              isOpen={!!configForDeletion}
              onCancel={() => setConfigForDeletion(undefined)}
              onConfirm={() => {
                setConfigForDeletion(undefined);
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    ...state.config,
                    config: {
                      ...state.config.config,
                      gcp: {
                        ...state.config.config.gcp,
                        networkConfigs:
                          state.config.config.gcp?.networkConfigs?.filter(
                            (x) => x !== configForDeletion
                          ),
                      },
                    },
                  },
                }));
              }}
            />
            {showVpcSelectionDialog && (
              <GcpNetworkSelectionDialog
                config={
                  props.wizardState.config.config.gcp?.networkConfigs || []
                }
                anchorEl={addCustomNetworkSettingsButtonEl}
                accountId={props.wizardState.account.id}
                onClose={(network) => {
                  setShowVpcSelectionDialog(false);
                  if (
                    !network ||
                    props.wizardState.config.config.gcp?.networkConfigs?.some(
                      (x) =>
                        x.network === network.networkName &&
                        x.networkHostProject === network.project
                    )
                  ) {
                    return;
                  }

                  const config: GcpNetworkConnectivityConfig = {
                    network: network.networkName,
                    networkHostProject: network.project,
                    isSharedVpc: network.isShared,
                  };

                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      ...state.config,
                      config: {
                        ...state.config.config,
                        gcp: {
                          ...state.config.config.gcp,
                          networkConfigs: [
                            ...(state.config.config.gcp?.networkConfigs || []),
                            config,
                          ],
                        },
                      },
                    },
                  }));
                  setSelectedConfig(config);
                  setEditedConfig(config);
                }}
              />
            )}
          </Stack>
          {!!props.wizardState.config.config.gcp?.networkConfigs?.length && (
            <Divider flexItem orientation='vertical' />
          )}
          {selectedConfig && (
            <Stack className='ml-[40px]' gap='40px' flexGrow={1}>
              <Stack direction='row' justifyContent='space-between'>
                <Stack direction='row' gap='4px'>
                  <Typography component='span'>{`Restore server in`}</Typography>
                  <Typography component='span' className='font-semibold'>
                    {selectedConfig.network}
                  </Typography>
                  <Typography component='span'>{`will use these settings:`}</Typography>
                </Stack>
                <ButtonWithIcon
                  variant='outlined'
                  icon='material-symbols-stylus-outline'
                  text='Edit Network'
                  onClick={() => setEditedConfig(selectedConfig)}
                />
              </Stack>
              <Stack direction='row' gap='100px'>
                <Stack gap='36px' className='w-[240px]'>
                  <Typography className='font-semibold'>
                    Subnets per region
                  </Typography>
                  <Typography lineHeight={'20px'} fontWeight={300}>
                    {`The restore server will launch in the subnets you specify. Make sure outbound access is open in the selected subnets.`}
                  </Typography>
                </Stack>
                <Stack
                  flexGrow={1}
                  gap='24px'
                  divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                >
                  {(regions?.regions || []).sort().map((region) => {
                    const subnetId = selectedConfig.subnetsPerRegion?.find(
                      (y) => y.region === region
                    )?.subnetName;

                    return (
                      <Stack key={region}>
                        <Stack direction='row' alignItems='center' gap='40px'>
                          <Typography className='w-[150px]'>
                            {region}
                          </Typography>
                          {subnetId && <Typography>{subnetId}</Typography>}
                          {!subnetId && (
                            <Typography
                              color={'var(--mui-palette-text-disabled)'}
                            >
                              Default subnet
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
              <Divider className='my-[20px]' />
            </Stack>
          )}
        </Stack>
      </Stack>
      {editedConfig && (
        <GcpCustomizeNetworkBottomDialog
          accountId={props.wizardState.account.id}
          config={editedConfig}
          regions={regions?.regions || []}
          onCancel={() => setEditedConfig(undefined)}
          onSave={(updatedConfig) => {
            props.setWizardState((state) => ({
              ...state,
              config: {
                ...state.config,
                config: {
                  ...state.config.config,
                  gcp: {
                    ...state.config.config.gcp,
                    networkConfigs:
                      state.config.config.gcp?.networkConfigs?.map((x) =>
                        x === editedConfig ? updatedConfig : x
                      ),
                  },
                },
              },
            }));
            setSelectedConfig(updatedConfig);
            setEditedConfig(undefined);
          }}
        />
      )}
    </StepContainer>
  );
};
